import { ReactElement, SyntheticEvent, useEffect, useState } from 'react'
import {
  BackButton,
  Input,
  FlexWrapper,
  EditableDropdown,
  PageWrapper,
  ActionButton,
  Button,
  SectionTitle,
  DropdownWrapper
} from '../../../../components'
import 'react-datepicker/dist/react-datepicker.css'
import { useHistory, useLocation } from 'react-router-dom'
import strings from '../../../../locale/en'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { Admin } from '../../../../redux/coachingCenter/types'
import { salutationList, statusList } from './const'
import {
  getBatches,
  getBranches,
  getInstitutes,
  selectAdmin
} from '../../../../redux/coachingCenter/actions'
import {
  saveUserDetail,
  editUserDetail
} from '../../../../redux/userDetail/actions'
import validateForm from './validateForm'
import { UserDetailField } from './typings'
import { hasFormError } from '../../../../helpers/formValidation'
import AdminType, { AdminId } from '../../../../const/admin'
import {
  getBatchDropdown,
  getBranchDropdown,
  getCoursesDropdown,
  getInstituteDropdown
} from '../../../../helpers'
import { DropdownListProps } from '../../../../components/EditableDropdown/typings'
import { resetBranches } from '../../../../redux/branch/action'
import { InputGroup } from 'react-bootstrap'
import { InputWrapper } from '../../../../components/Input/subcomponents'
import { resetBatches } from '../../../../redux/batch/action'
import { ROLE } from '../../../../helpers/determineMenu'
import DatePicker from 'react-datepicker'
import { format } from 'date-fns'
import { DATE_FORMAT_YYYYMMDD } from '../../../../const/dateFormat'
import { DatePickerWrapper, FormBackWrapper } from '../subcomponents'

interface LocationState {
  userType: string
  isEdit?: string
}

const NewUser = (): ReactElement => {
  const { selectedAdmin, institutes, branches, userRole, batches, userDetail } =
    useSelector(
      (state: RootState) => ({
        selectedAdmin: state.coachingCenter.selectedAdmin as Admin,
        institutes: state.coachingCenter.institutes,
        branches: state.coachingCenter.branches,
        userRole: state.user.userInfo?.role,
        batches: state.coachingCenter.batches,
        userDetail: state.user?.userInfo?.userDetail
      }),
      shallowEqual
    )
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { userType: type, isEdit } = location.state as LocationState
  const initialValue = type
    ? { ...selectedAdmin, userType: type }
    : { ...selectedAdmin }
  const [studentAccess, setStudentAccess] = useState(false)

  const [values, setValues] = useState(initialValue)
  const [errors, setErrors] = useState({} as Record<string, string>)
  const instituteList = institutes ? getInstituteDropdown(institutes) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []
  const courses = batches.map((batch) => batch.course).flat()
  const courseList = courses ? getCoursesDropdown(courses) : []
  const defaultInstituteId = userDetail?.coachingCenterId || ''

  const {
    userDetail: {
      fatherName,
      userName,
      userType,
      salutation,
      lastName,
      firstName,
      qualification,
      mobileNumber,
      email,
      dob,
      address1,
      address2,
      password,
      country,
      state,
      city,
      zipcode,
      status,
      profileImage,
      instituteName,
      branchName,
      subjectName,
      yearOfExperience,
      enrollmentNumber
    },
    menu: {
      admin: { selectBranch, selectBatch, selectCourse, selectCoachingCenter }
    },
    button: { save, back }
  } = strings

  const validateField = (field: UserDetailField): void => {
    setErrors(
      validateForm({
        values,
        errors,
        field
      })
    )
  }

  const getRoleId = (): string => {
    switch (values.userType) {
      case AdminType.SUPERADMIN:
        return AdminId.SUPERADMIN
      case AdminType.BRANCHADMIN:
        return AdminId.BRANCHADMIN
      case AdminType.INSTITUTEADMIN:
        return AdminId.INSTITUTEADMIN
      case AdminType.TEACHER:
        return AdminId.TEACHER
      case AdminType.STUDENT:
        return AdminId.STUDENT
      default:
        return ''
    }
  }

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault()

    const validationError = validateForm({
      values,
      errors
    })

    if (hasFormError(validationError)) {
      setErrors(validationError)
    } else {
      const roleId = getRoleId()
      const { user, ...data } = values
      const reqData = {
        ...data,
        userName: user.userName,
        roleId
      }

      isEdit
        ? dispatch(editUserDetail(reqData))
        : dispatch(saveUserDetail(reqData))
    }
  }

  const resetValues = () => {
    dispatch(resetBranches())
    dispatch(resetBatches())
  }

  const handleInstituteSelect = (institue: DropdownListProps) => {
    dispatch(
      getBranches({
        coachingCentreId: institue?.id,
        type: values.userType as AdminType
      })
    )
    setValues({
      ...values,
      coachingCenterId: institue?.id
    })
    resetValues()
    setErrors(
      validateForm({
        values: { ...values, coachingCenterId: institue?.id },
        errors,
        field: 'instituteName'
      })
    )
  }

  const handleBranchSelect = (branch: DropdownListProps) => {
    const { coachingCenterId, userType } = values
    if (
      userType === AdminType.TEACHER ||
      userType === AdminType.STUDENT ||
      userType === AdminType.BRANCHADMIN
    ) {
      dispatch(resetBatches())
      dispatch(
        getBatches({
          coachingCentreId: coachingCenterId || '',
          branchId: branch?.id,
          type: userType as AdminType
        })
      )
    }
    setValues({
      ...values,
      branchIds: [branch?.id]
    })
    setErrors(
      validateForm({
        values: { ...values, branchIds: [branch?.id] },
        errors,
        field: 'branchName'
      })
    )
  }

  const handleBatchSelect = (batch: DropdownListProps) => {
    setValues({ ...values, batchIds: [batch?.id] })
    setErrors(
      validateForm({
        values: { ...values, batchIds: [batch?.id] },
        errors,
        field: 'batchName'
      })
    )
  }

  const handleCourseSelect = (course: DropdownListProps) => {
    setValues({
      ...values,
      courseId: [course]
    })
  }

  useEffect(() => {
    if (isEdit) {
      dispatch(
        getBranches({
          coachingCentreId: values.coachingCenterId || '',
          type: values.userType as AdminType
        })
      )

      dispatch(
        getBatches({
          coachingCentreId: values.coachingCenterId || '',
          branchId: values?.branchIds?.[0] || '',
          type: userType as AdminType
        })
      )
    } else {
      dispatch(selectAdmin(null))
      resetValues()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  useEffect(() => {
    setValues({ ...selectedAdmin, userType: type })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdmin])

  useEffect(() => {
    const { userType, coachingCenterId } = values

    if (userRole === ROLE.INSTITUTE_ADMIN) {
      dispatch(
        getBranches({
          coachingCentreId: defaultInstituteId,
          type: values.userType as AdminType
        })
      )
      setValues({
        ...values,
        coachingCenterId: defaultInstituteId
      })
    } else {
      userType !== AdminType.SUPERADMIN && dispatch(getInstitutes())
    }

    if (coachingCenterId) {
      dispatch(
        getBranches({
          coachingCentreId: coachingCenterId,
          type: userType as AdminType
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shouldShowRole =
    userRole === ROLE.ADMIN ||
    userRole === ROLE.BRANCH_ADMIN ||
    userRole === ROLE.INSTITUTE_ADMIN
  const shouldShowType =
    values?.userType === AdminType.INSTITUTEADMIN ||
    values?.userType === AdminType.BRANCHADMIN

  console.log(values?.branchIds)

  return (
    <PageWrapper id="container">
      <FlexWrapper noMargin>
        <BackButton
          handleBack={() => {
            dispatch(selectAdmin(null))
            history.goBack()
          }}
        />
        <SectionTitle
          title={
            values?.userType.toLowerCase() === 'coach'
              ? 'Teacher'
              : `Add ${values?.userType.toLowerCase()}`
          }
        />
      </FlexWrapper>
      <FormBackWrapper>
        <FlexWrapper justifyContent="space-between">
          <EditableDropdown
            isDisabled={!!values || type}
            width="30%"
            title={userType}
            isRequired
            dropdownList={[]}
            handleSelect={() => {}}
            placeholder="Select User Type"
            defaultValue={{
              id: '',
              name: values?.userType || ''
            }}
          />
          <Input
            label={userName}
            isRequired
            value={values?.user?.userName || ''}
            width="30%"
            onBlur={() => validateField('userName')}
            error={errors.userName}
            placeholder={userName}
            onChange={(value: string) =>
              setValues({
                ...values,
                user: { ...values?.user, userName: value }
              })
            }
          />
          {values.userType !== AdminType.SUPERADMIN &&
            userRole !== ROLE.INSTITUTE_ADMIN && (
              <EditableDropdown
                width="30%"
                title={instituteName}
                isRequired
                dropdownList={instituteList}
                placeholder={selectCoachingCenter}
                error={errors.instituteName}
                onBlur={() => validateField('instituteName')}
                handleSelect={(institue) => handleInstituteSelect(institue)}
                defaultValue={{
                  id: values?.coachingCenterId || '',
                  name: values?.coachingCenterId
                    ? instituteList.filter(
                        (institue) => institue?.id === values?.coachingCenterId
                      )[0]?.name
                    : ''
                }}
              />
            )}
          {shouldShowRole && values?.userType === AdminType.INSTITUTEADMIN && (
            <EditableDropdown
              width="30%"
              margin="0 0 16px"
              title={branchName}
              isRequired
              dropdownList={branchList}
              placeholder={selectBranch}
              onBlur={() => validateField('branchName')}
              error={errors.branchName}
              isMultiChoice
              handleMultiSelect={(branch) => {
                const ids = branch.map((item: DropdownListProps) => item?.id)
                const { coachingCenterId, userType } = values
                dispatch(
                  getBatches({
                    coachingCentreId: coachingCenterId || '',
                    branchId: ids,
                    type: userType as AdminType
                  })
                )
                setValues({
                  ...values,
                  branchIds: ids
                })
              }}
              defaultValue={{
                id: values?.branchIds?.[0] || '',
                name: values?.branchIds?.[0]
                  ? branchList?.filter(
                      (branch) => branch?.id === values?.branchIds?.[0]
                    )[0]?.name
                  : ''
              }}
            />
          )}
          {values.userType !== AdminType.SUPERADMIN &&
            values.userType !== AdminType.INSTITUTEADMIN && (
              <EditableDropdown
                width="30%"
                title={branchName}
                isRequired
                dropdownList={branchList}
                placeholder={selectBranch}
                onBlur={() => validateField('branchName')}
                error={errors.branchName}
                handleSelect={(branch) => handleBranchSelect(branch)}
                defaultValue={{
                  id: values?.branchIds?.[0] || '',
                  name: values?.branchIds?.[0]
                    ? branchList?.filter(
                        (branch) => branch?.id === values?.branchIds?.[0]
                      )[0]?.name
                    : ''
                }}
              />
            )}
          {shouldShowType && (
            <DropdownWrapper width="30%">
              <EditableDropdown
                dropdownList={batchList}
                title={selectBatch}
                placeholder={selectBatch}
                margin="0 0 16px"
                isRequired
                onBlur={() => validateField('batchName')}
                error={errors.batchName}
                isMultiChoice
                handleMultiSelect={(batch) => {
                  const ids = batch.map((item: DropdownListProps) => item?.id)
                  setValues({
                    ...values,
                    batchIds: ids
                  })
                }}
                defaultValue={{
                  id: values?.batchIds?.[0] || '',
                  name: values?.batchIds?.[0]
                    ? batchList?.filter(
                        (batch) => batch?.id === values?.batchIds?.[0]
                      )[0]?.name
                    : ''
                }}
              />
            </DropdownWrapper>
          )}
          {(values.userType === AdminType.TEACHER ||
            values.userType === AdminType.STUDENT) && (
            <EditableDropdown
              width="30%"
              dropdownList={batchList}
              title={selectBatch}
              placeholder={selectBatch}
              isRequired
              onBlur={() => validateField('batchName')}
              error={errors.batchName}
              handleSelect={(batch) => handleBatchSelect(batch)}
              defaultValue={{
                id: values?.batchIds?.[0] || '',
                name: values?.batchIds?.[0]
                  ? batchList?.filter(
                      (batch) => batch?.id === values?.batchIds?.[0]
                    )[0]?.name
                  : ''
              }}
            />
          )}
          {values.userType === AdminType.STUDENT && (
            <EditableDropdown
              width="30%"
              dropdownList={courseList}
              title={selectCourse}
              placeholder={selectCourse}
              handleSelect={(course) => handleCourseSelect(course)}
              defaultValue={{
                id: values?.courseId?.[0]?.id || '',
                name: values?.courseId
                  ? courseList.filter(
                      (course) => course?.id === values?.courseId?.[0]?.id
                    )[0]?.name
                  : ''
              }}
            />
          )}
          {values.userType === AdminType.TEACHER && (
            <Input
              label={subjectName}
              value={values?.subject || ''}
              width="30%"
              isRequired
              onBlur={() => validateField('subject')}
              error={errors.subject}
              placeholder={subjectName}
              onChange={(subject: string) => setValues({ ...values, subject })}
            />
          )}
          <EditableDropdown
            width="30%"
            title={salutation}
            dropdownList={salutationList}
            placeholder="Select a salutation"
            handleSelect={({ name }) => {
              setValues({ ...values, salutation: name })
            }}
            defaultValue={{
              id: values?.salutation
                ? salutationList.filter(
                    (salutation) => salutation?.name === values?.salutation
                  )[0]?.id
                : '',
              name: values?.salutation || ''
            }}
          />
          <Input
            label={firstName}
            isRequired
            value={values?.firstName || ''}
            width="30%"
            placeholder={firstName}
            onBlur={() => validateField('firstName')}
            error={errors.firstName}
            onChange={(firstName: string) =>
              setValues({ ...values, firstName })
            }
          />
          <Input
            label={lastName}
            isRequired
            value={values?.lastName || ''}
            width="30%"
            placeholder={lastName}
            onBlur={() => validateField('lastName')}
            error={errors.lastName}
            onChange={(lastName: string) => setValues({ ...values, lastName })}
          />
          <Input
            label={mobileNumber}
            isRequired
            value={values?.mobileNumber || ''}
            width="30%"
            placeholder={mobileNumber}
            onBlur={() => validateField('mobileNumber')}
            error={errors.mobileNumber}
            onChange={(mobileNumber: string) =>
              setValues({ ...values, mobileNumber })
            }
          />
          <Input
            label={email}
            value={values?.email || ''}
            width="30%"
            isRequired
            onBlur={() => validateField('email')}
            error={errors.email}
            placeholder={email}
            onChange={(email: string) => setValues({ ...values, email })}
          />
          <DatePickerWrapper>
            <DatePicker
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              selected={values.dob ? new Date(values.dob) : new Date()}
              onSelect={(date: Date) => {
                setValues({
                  ...values,
                  dob: date ? format(date, DATE_FORMAT_YYYYMMDD) : ''
                })
              }}
              onChange={(date: Date) => {
                setValues({
                  ...values,
                  dob: date ? format(date, DATE_FORMAT_YYYYMMDD) : ''
                })
              }}
              placeholderText={dob}
              customInput={
                <Input
                  value={values.dob}
                  label={dob}
                  placeholder={dob}
                  suffix={['far', 'calendar']}
                  onChange={(value: string) => {
                    setValues({ ...values, dob: value })
                  }}
                />
              }
            />
          </DatePickerWrapper>
          <Input
            label={fatherName}
            isRequired
            value={values?.fatherName || ''}
            width="30%"
            placeholder={fatherName}
            onBlur={() => validateField('fatherName')}
            error={errors.fatherName}
            onChange={(fatherName: string) =>
              setValues({ ...values, fatherName })
            }
          />
          <Input
            label={qualification}
            value={values?.qualification || ''}
            width="30%"
            placeholder={qualification}
            onChange={(qualification: string) =>
              setValues({ ...values, qualification })
            }
          />
          {values.userType === AdminType.TEACHER && (
            <Input
              label={yearOfExperience}
              value={values?.yearOfExperience || ''}
              width="30%"
              isRequired
              onBlur={() => validateField('yearOfExperience')}
              error={errors.yearOfExperience}
              placeholder={yearOfExperience}
              onChange={(yearOfExperience: string) =>
                setValues({ ...values, yearOfExperience })
              }
            />
          )}
          <Input
            label={address1}
            value={values?.address1 || ''}
            width="30%"
            placeholder={address1}
            onChange={(address1: string) => setValues({ ...values, address1 })}
          />
          <Input
            label={address2}
            value={values?.address2 || ''}
            width="30%"
            placeholder={address2}
            onChange={(address2: string) => setValues({ ...values, address2 })}
          />
          <Input
            label={country}
            value={values?.country || ''}
            width="30%"
            placeholder={country}
            onChange={(country: string) => setValues({ ...values, country })}
          />
          <Input
            label={state}
            value={values?.state || ''}
            width="30%"
            placeholder={state}
            onChange={(state: string) => setValues({ ...values, state })}
          />
          <Input
            label={city}
            value={values?.city || ''}
            width="30%"
            placeholder={city}
            onChange={(city: string) => setValues({ ...values, city })}
          />
          <Input
            label={zipcode}
            value={values?.zipCode || ''}
            width="30%"
            placeholder={zipcode}
            onChange={(zipCode: string) => setValues({ ...values, zipCode })}
          />
          {values.userType === AdminType.STUDENT && (
            <Input
              label={enrollmentNumber}
              value={values?.enrollmentNumber || ''}
              width="30%"
              placeholder={enrollmentNumber}
              onChange={(enrollmentNumber: string) =>
                setValues({ ...values, enrollmentNumber })
              }
            />
          )}
          <Input
            label={profileImage}
            value=""
            width="30%"
            inputType="file"
            accept=".img,.jpeg,.png,.jpg"
            placeholder={profileImage}
            onChange={(profileImagePath: string) =>
              setValues({ ...values, profileImagePath })
            }
          />
          <EditableDropdown
            width="30%"
            title={status}
            isRequired
            dropdownList={statusList}
            handleSelect={({ name }) => {
              setValues({ ...values, status: name })
              setErrors(
                validateForm({
                  values: { ...values, status: name },
                  errors,
                  field: 'status'
                })
              )
            }}
            onBlur={() => validateField('status')}
            error={errors.status}
            placeholder="Select status"
            defaultValue={{
              id: '',
              name: values?.status || ''
            }}
          />
          <Input
            label={password}
            value={values?.password || ''}
            width="30%"
            placeholder={password}
            onChange={(password: string) => setValues({ ...values, password })}
          />
          {((userRole === AdminType.SUPERADMIN &&
            values.userType === AdminType.BRANCHADMIN) ||
            values.userType === AdminType.INSTITUTEADMIN) && (
            <InputWrapper width="30%">
              <InputGroup className="mt-4">
                <InputGroup.Checkbox
                  onChange={() => {
                    setStudentAccess(!studentAccess)
                  }}
                />
                <InputGroup.Text>Student Access</InputGroup.Text>
              </InputGroup>
            </InputWrapper>
          )}
        </FlexWrapper>
        <FlexWrapper justifyContent="center">
          <ActionButton
            variant="outline-secondary"
            onClick={() => {
              dispatch(selectAdmin(null))
              history.goBack()
            }}
          >
            {back}
          </ActionButton>
          <Button
            onClick={(e: SyntheticEvent) => {
              dispatch(selectAdmin(null))
              handleSubmit(e)
            }}
          >
            {save}
          </Button>
        </FlexWrapper>
      </FormBackWrapper>
    </PageWrapper>
  )
}

export default NewUser
